import React from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import ProductAvailability from "helpers/product-availability";
import SizeHelper from "helpers/form-field-size-helper";
import MembershipPickWayfind from "./membership-pick-wayfind";
import GridTopFiltersZipModal from "../new_taxonomy/grid-top-filters-zip-modal";
import { closeModal, openZipFilterModal } from "actions/grid-modal-actions";
import * as ProductsActions from "actions/products-actions";
import { productPropType } from "components/propTypes";
import { MODAL_NAMES } from "constants/modalNames";
import { displayModal } from "actions/shared-actions";
import { flagsAndExperimentNames } from "components/source/hoc/with-flags-and-experiments";
import RtrButton from "components/design-system/rtr-button";
import HeapHelpers from "helpers/heap-helpers";
import UrgencySignalWrapper, {
  calculateEligiblityToShowUrgencySignalOnLegacyPdp,
} from "../products/urgency-signal/wrapper";
import { useSubscriptionRejoinUrl } from "hooks/use-subscription-rejoin-url";
import { navigateTo } from "helpers/location-helpers";
import { isChurnedSubscriber } from "helpers/membership-helpers";
import Routes from "routes";
import CustomerPromise from "../shared/customer-promise";
import { usePersistedFilters } from "../hoc/with-persisted-filters";

/**
 * A container that displays available skus for subscription and renders an ATB CTA for prospects.
 * This CTA does not in reality add an item to bag, but drives users to the Plans page.
 */
const MembershipItemDummyATBContainer = ({
  handleWayfindCtaClick,
  isGridProductDrawer,
  product,
  renderSizeSelector,
  sizeSelected,
  skuIdSelected,
}) => {
  const dispatch = useDispatch();
  const requestOpenZipFilterModal = () => dispatch(openZipFilterModal());
  const closeZipFilterModal = () => dispatch(closeModal());
  const { writePreferences } = usePersistedFilters();

  const browser = useSelector(state => state.browser);
  const displayedModal = useSelector(state => state.displayedModal);
  const membershipState = useSelector(state => state.membershipState);
  const userData = useSelector(state => state.userData);
  const workingFilters = useSelector(state => state.workingFilters);
  const flagsAndExperiments = useSelector(state => state.flagsAndExperiments);
  const isChurned = useSelector(state => isChurnedSubscriber(state.membershipState));

  const { userCanRejoin, rejoinUrl } = useSubscriptionRejoinUrl();

  const saveZipFilters = newFilters => {
    const updatedFilters = {
      ...workingFilters,
      ...newFilters,
    };
    dispatch(ProductsActions.changeFilters(updatedFilters));
    writePreferences(updatedFilters);
  };

  const refreshDepotIdCookie = filters => {
    const zipCode = filters.zip_code;
    dispatch(ProductsActions.refreshMembershipDepotIdCookie(zipCode, saveZipFilters));
  };

  const onSubmitZip = zipFilter => {
    refreshDepotIdCookie(zipFilter);
    closeZipFilterModal();

    //If we're viewing product drawer on grid, we also need to reload the grid
    if (isGridProductDrawer) {
      const page = 1;
      dispatch(ProductsActions.refreshGridProducts(page));
    }
  };

  // Heads-up, if you are making changes in this file especially this function - please make sure you update the same
  // for universal PDP in the file `universal_pdp/pdp.jsx`. Feel free to reach out with any questions
  const renderAddToBagCta = () => {
    const hasNotSelectedSize = sizeSelected === "" && SizeHelper.hasProductSizes(product);
    const selectedSizeAvailable = ProductAvailability.hasSkuCount(sizeSelected, product, membershipState);
    const isOneSizeFitsAll = SizeHelper.isOneSizeFitsAll(product);
    const isAvailableOneSizeFitsAll = isOneSizeFitsAll && product.skus[0].unlimitedAvailabilities[0].count > 0;
    let buttonCopy =
      hasNotSelectedSize || selectedSizeAvailable || isAvailableOneSizeFitsAll ? "Add to Shipment" : "Add to Hearts";

    if (isChurned) {
      buttonCopy = "Reactivate Subscription";
    }

    const onClickAddToBag = e => {
      e.preventDefault();
      e.stopPropagation();

      const { isEligible, availabilityOfSelectedSku } = calculateEligiblityToShowUrgencySignalOnLegacyPdp(
        product?.skus,
        sizeSelected,
        flagsAndExperiments?.[flagsAndExperimentNames.RSV_1421_URGENCY_SIGNAL],
        userData,
        false,
        false
      );

      HeapHelpers.fireHeapEvent("non-subscriber-membership-atb", {
        sizeSelected,
        urgencySignalShown: isEligible,
        urgencySignalItemsLeft: availabilityOfSelectedSku,
        urgencySignalExperimentStatus: flagsAndExperiments?.[flagsAndExperimentNames.RSV_1421_URGENCY_SIGNAL],
      });

      if (userCanRejoin) {
        navigateTo(rejoinUrl);
      } else if (isChurned) {
        navigateTo(Routes.LP.plans);
      } else {
        dispatch(displayModal(MODAL_NAMES.PLANS_DRAWER));
      }
    };

    return (
      <div className="mt-4">
        <RtrButton fullWidth onClick={onClickAddToBag} disabled={hasNotSelectedSize && !isOneSizeFitsAll}>
          {buttonCopy}
        </RtrButton>
      </div>
    );
  };

  const renderSelectedZip = () => {
    const onClickZipCode = e => {
      e.preventDefault();
      e.stopPropagation();
      requestOpenZipFilterModal();
    };

    return (
      <div className="my-4">
        Delivery to{" "}
        <button className="underline font-bold" onClick={onClickZipCode}>
          {workingFilters.zip_code}
        </button>
      </div>
    );
  };

  const renderReservePdpLink = () => {
    return (
      <MembershipPickWayfind
        handleWayfindCtaClick={handleWayfindCtaClick}
        manuallyShowOption="reserve"
        membershipState={membershipState}
        product={product}
        userData={userData}
      />
    );
  };

  const renderZipFilterModal = () => {
    return (
      <GridTopFiltersZipModal
        browser={browser}
        displayedModal={displayedModal}
        onRequestClose={closeZipFilterModal}
        onSubmit={onSubmitZip}
        workingFilters={workingFilters}
        key="gridTopFiltersZipModal"
      />
    );
  };

  return (
    <section
      className={`membership-form membership-form-fields ${
        flagsAndExperiments?.[flagsAndExperimentNames.RSV_1494_ATB_TABS] ? "!mt-0" : ""
      }`}>
      {renderSizeSelector()}
      <UrgencySignalWrapper selectedSize={sizeSelected || skuIdSelected} className="py-2.5" />
      {renderAddToBagCta()}
      <CustomerPromise />
      {renderSelectedZip()}
      {renderReservePdpLink()}
      {renderZipFilterModal()}
    </section>
  );
};

MembershipItemDummyATBContainer.propTypes = {
  handleWayfindCtaClick: PropTypes.func.isRequired,
  isGridProductDrawer: PropTypes.bool,
  product: productPropType.isRequired,
  renderSizeSelector: PropTypes.func.isRequired,
  sizeSelected: PropTypes.string,
  skuIdSelected: PropTypes.string,
};

export default MembershipItemDummyATBContainer;
